// log-row-helpers.ts — DOM helpers + dedupe state for log row insertion.
// Used by roll-insert.ts (all) and roll-group.ts (_ftFindLogRowByRollId).

const FT_LOG_VISIBLE_ROW_CAP = 100;
const _ftRecentRollIsForDedupe = new Set<string>();
const _ftRecentRollIsOrder: string[] = [];

function _ftFindLogRowByRollId(rollId: string): HTMLElement | null {
    const ul = document.querySelector<HTMLElement>('#div_sidelog_results > ul.list-group');
    if (!ul) return null;
    // Use attribute selector; CSS.escape handles any UUID format weirdness.
    const safe = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(rollId) : rollId.replace(/"/g, '\\"');
    return ul.querySelector<HTMLElement>(`:scope > li[data-ft-roll-id="${safe}"]`);
}

// Phase 3.7 — push-path timestamp suppression. When a fresh push arrives whose roll_id
// matches the current top row's roll_id, that top row is about to become the SECOND row
// of a same-roll_id cluster. PHP's $grouped_roll_ids (log_render_helpers_inc.php:438-453)
// would have suppressed its timestamp footer; mirror that in the push path so a Sacred-
// Flame-style burst reads as one card stack instead of N independent cards. Call before
// each prependLogRow(...) site in handleRollInsert.
function _ftStripPrevRowTimestampIfSameRollId(rollId: string | undefined): void {
    if (!rollId) return;
    const ul = document.querySelector('#div_sidelog_results > ul.list-group');
    if (!ul) return;
    const prev = ul.firstElementChild;
    if (!prev || prev.getAttribute('data-ft-roll-id') !== rollId) return;
    // Same class signature as PHP server-render (log_render_helpers_inc.php:652) and JS
    // renderLogTimestamp (log-render.js). `:scope >` ensures we only strip the row's own
    // footer, not any timestamp nested deeper in a collapse.
    const footer = prev.querySelector(':scope > .row.mb-2.px-2.border-bottom.border-start.border-end.rounded-bottom');
    if (footer) footer.remove();
}

function prependLogRow(html: string): void {
    let ul = document.querySelector<HTMLElement>('#div_sidelog_results > ul.list-group');
    if (!ul) {
        // Lazy-init the list-group structure that log_display_inc.php would normally
        // emit on full load. buildLog() only fires from loadEncounter() (and a few
        // explicit callers), so a user rolling the quick-roller before opening an
        // encounter has an empty #div_sidelog_results — without this, the first push
        // has nothing to prepend to. The container only ever holds the register/login
        // fallback for unverified users, and unverified users can't trigger inserts,
        // so it's safe to assume an empty container here for the push path.
        const container = document.getElementById('div_sidelog_results');
        if (!container) return;
        if (container.querySelector('ul.list-group') == null) {
            // Preserve any existing children (e.g. dev artifacts) above the new list.
            if (container.innerHTML.trim() === '') {
                container.innerHTML = '<ul class="list-group list-group-flush"></ul>';
            } else {
                container.insertAdjacentHTML('afterbegin', '<ul class="list-group list-group-flush"></ul>');
            }
        }
        ul = container.querySelector<HTMLElement>('ul.list-group');
        if (!ul) return;
    }
    ul.insertAdjacentHTML('afterbegin', html);

    // Re-init Bootstrap tooltips on freshly-injected nodes. `:scope > li:first-child`
    // targets just the new row(s); the chunk may include one mass-contest <li> followed
    // by the row <li>, so we walk all newly-injected descendants under the first child.
    if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
        const first = ul.firstElementChild;
        if (first) {
            first.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
                try { new bootstrap.Tooltip(el); } catch (_) { /* ignore double-init */ }
            });
        }
    }

    // Hydrate any rune-icon mount the new row delivered — e.g. an ability/spell-sourced status's L1
    // sigil in a status_effect_expired/_paused/_resumed row (log-render.ts statusLifecycleIconHtml).
    // The log is otherwise pure-static HTML (real <img>/iconMap strings), so a rune mount is the only
    // push-path element that needs JS hydration. Scoped to the freshly-prepended node; idempotent
    // (the scanner skips already-mounted placeholders). No-op when the rune bundle isn't loaded.
    const firstEl = ul.firstElementChild as HTMLElement | null;
    if (firstEl && typeof window.ftMountRuneIcons === 'function') {
        try { window.ftMountRuneIcons(firstEl); } catch (_) { /* rune bundle absent */ }
    }
}

function trimLogRows(max: number): void {
    const ul = document.querySelector('#div_sidelog_results > ul.list-group');
    if (!ul) return;
    while (ul.children.length > max) {
        const row = ul.lastElementChild!;
        window.disposeBootstrapTooltipsWithin?.(row);
        row.remove();
    }
}

export {
    FT_LOG_VISIBLE_ROW_CAP,
    _ftRecentRollIsForDedupe,
    _ftRecentRollIsOrder,
    _ftFindLogRowByRollId,
    _ftStripPrevRowTimestampIfSameRollId,
    prependLogRow,
    trimLogRows,
};
